import { OrderedSet } from 'immutable'
import { Moment } from 'moment'
import * as React from 'react'
import { FormattedNumber } from 'react-intl'
import {
  Action,
  any,
  async,
  fromJSX,
  none,
  nothing,
  some,
  stateful,
  Widget
} from 'widgets-for-react'
import { LoaderCircle } from '../../accommodations/models/loading'
import { PatternMatcher } from '../../utils/pattern_matcher'

import { SettlementsOverviewLayout } from './settlementsLayout'
import {
  initialState,
  PagedSettlements,
  Props,
  Settlement,
  settlementsOverviewUpdaters,
  SettlementSpecificationWithUserIds,
  State
} from './settlementsState'

const currencyWidget = (x: number) =>
  fromJSX(_ => (
    <FormattedNumber value={x / 100} style="currency" currency="EUR" />
  ))

const dateWidget = (x: Moment) =>
  fromJSX(_ => <>{!!x ? x.format('DD-MM-YYYY') : x}</>)

export const SettlementAttributeRenderWidgets: PatternMatcher<
  keyof Settlement,
  (_: Settlement) => Widget<{}>
> = {
  uid: x => fromJSX(_ => <>{x.uid}</>),
  amount_paid: x => currencyWidget(x.amount_paid),
  amount_payable: x => currencyWidget(x.amount_payable),
  period_start: x => dateWidget(x.period_start),
  period_end: x => dateWidget(x.period_end),
  created: x => dateWidget(x.created),
  updated: x => dateWidget(x.updated),
  status: x => fromJSX(_ => <>{x.status}</>),
  paid: x => fromJSX(_ => <>{x.paid}</>),
  total_number_of_transactions: x =>
    fromJSX(_ => <>{x.total_number_of_transactions}</>),
  number_of_transactions: x => fromJSX(_ => <>{x.number_of_transactions}</>),
  number_of_refunds: x => fromJSX(_ => <>{x.number_of_refunds}</>),
  number_of_withdrawals: x => fromJSX(_ => <>{x.number_of_withdrawals}</>),
  number_of_mandates: x => fromJSX(_ => <>{x.number_of_mandates}</>),
  number_of_internal_transfers: x =>
    fromJSX(_ => <>{x.number_of_internal_transfers}</>),
  number_of_multi_transactions: x =>
    fromJSX(_ => <>{x.number_of_multi_transactions}</>),
  total_volume: x => currencyWidget(x.total_volume),
  transaction_volume: x => currencyWidget(x.transaction_volume),
  refund_volume: x => currencyWidget(x.refund_volume),
  withdrawal_volume: x => currencyWidget(x.withdrawal_volume),
  mandate_volume: x => currencyWidget(x.mandate_volume),
  internal_transfer_volume: x => currencyWidget(x.internal_transfer_volume),
  multi_transaction_volume: x => currencyWidget(x.multi_transaction_volume),
  total_transaction_costs: x => currencyWidget(x.total_transaction_costs),
  transaction_costs: x => currencyWidget(x.transaction_costs),
  refund_costs: x => currencyWidget(x.refund_costs),
  withdrawal_costs: x => currencyWidget(x.withdrawal_costs),
  mandate_costs: x => currencyWidget(x.mandate_costs),
  internal_transfer_costs: x => currencyWidget(x.internal_transfer_costs),
  multi_transaction_costs: x => currencyWidget(x.multi_transaction_costs),
  total_order_fees: x => currencyWidget(x.total_order_fees),
  total_refund_fees: x => currencyWidget(x.total_refund_fees),
  total_gateway_fees: x => currencyWidget(x.total_gateway_fees),
  total_amount: x => currencyWidget(x.total_amount),
  payout_type: x => fromJSX(_ => <>{x.payout_type}</>)
}

export const SettlementAttributesForColumn = (): OrderedSet<keyof Settlement> =>
  OrderedSet(['amount_paid', 'amount_payable', 'period_start', 'period_end'])

const SettlementAttributesForDetails = (): OrderedSet<keyof Settlement> =>
  OrderedSet([
    'uid',
    'amount_paid',
    'amount_payable',
    'period_start',
    'period_end',
    'created',
    'updated',
    'status',
    'paid',
    'total_number_of_transactions',
    'number_of_transactions',
    'number_of_refunds',
    'number_of_withdrawals',
    'number_of_mandates',
    'number_of_internal_transfers',
    'number_of_multi_transactions',
    'total_volume',
    'transaction_volume',
    'refund_volume',
    'withdrawal_volume',
    'mandate_volume',
    'internal_transfer_volume',
    'multi_transaction_volume',
    'total_transaction_costs',
    'transaction_costs',
    'refund_costs',
    'withdrawal_costs',
    'mandate_costs',
    'internal_transfer_costs',
    'multi_transaction_costs',
    'total_order_fees',
    'total_refund_fees',
    'total_gateway_fees',
    'total_amount'
  ])

export const SettlementDetails = (props: {
  settlement: Settlement
  onExportRequest: () => void
  isExportingSomething: () => boolean
  isExportingThis: () => boolean
}) => (
  <div className="settlement-details">
    {props.isExportingThis() ? (
      <LoaderCircle />
    ) : props.isExportingSomething() ? (
      <></>
    ) : (
      <button
        className="btn btn--primary"
        onClick={_ => props.onExportRequest()}
      >
        Export complete specifications
      </button>
    )}
    <table className="table--bordered">
      <tbody>
        {SettlementAttributesForDetails()
          .map(x => (
            <tr key={x}>
              <td className="cell-33">{x}</td>
              <td>
                {SettlementAttributeRenderWidgets[x](props.settlement).run(
                  _ => null
                )}
              </td>
            </tr>
          ))
          .toArray()}
      </tbody>
    </table>
  </div>
)

export class SettlementsOverview extends React.Component<Props> {
  render() {
    return (
      <>
        {stateful<State>({ key: 'settlements-overview' })(s0 =>
          any<Action<State>>()([
            s0.kind === 'loading'
              ? async<PagedSettlements>()(s0.overview).map(a =>
                  settlementsOverviewUpdaters.onOverviewUpdate(a)
                )
              : nothing(),
            s0.kind === 'overview' && s0.settlementExport.v.kind === 'r'
              ? async<SettlementSpecificationWithUserIds[]>()(
                  s0.settlementExport.v.v.specifications
                ).map(a => settlementsOverviewUpdaters.onExportUpdate(a))
              : nothing(),
            fromJSX(setState => (
              <SettlementsOverviewLayout
                originalProps={this.props}
                state={s0}
                onSettlementExport={settlement =>
                  setState(
                    settlementsOverviewUpdaters.onSettlementExport(settlement)
                  )
                }
                isExporting={() =>
                  s0.kind === 'overview' &&
                  s0.settlementExport.v.kind === 'r' &&
                  s0.settlementExport.v.v.specifications.kind === 'loading'
                }
                isExportingSettlement={s =>
                  s0.kind === 'overview' &&
                  s0.settlementExport.v.kind === 'r' &&
                  s0.settlementExport.v.v.specifications.kind === 'loading' &&
                  s0.settlementExport.v.v.settlement.uid === s.uid
                }
                toggleSettlement={settlement =>
                  setState(
                    settlementsOverviewUpdaters.onToggleSettlement(s =>
                      s.v.kind === 'r' && s.v.v.uid === settlement.uid
                        ? none()
                        : some({
                            uid: settlement.uid
                          })
                    )
                  )
                }
                onPageChange={page =>
                  setState(settlementsOverviewUpdaters.onPageChange(_ => page))
                }
              />
            ))
          ]).map(a => settlementsOverviewUpdaters.stateUpdater(a)(s0))
        )(initialState()).run(_ => null)}
      </>
    )
  }
}
